import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";

const API_URL = process.env.REACT_APP_API_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  // const { token } = useParams(); // Extract the token from the URL
  // console.log(useParams());
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [toastList, setToastList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const passwordsMatch =
    newPassword && confirmNewPassword && newPassword === confirmNewPassword;

  const showPasswordMismatch =
    confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;

  const passwordRequirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    specialChar: /[@$!%*?&#]/.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description:
            "Weak password! Must have 8+ characters, uppercase, lowercase, number, and special character.",
          variant: "destructive",
        },
      ]);
      return;
    }

    const passwordResetToken =
      document.getElementById("passwordResetToken").value; // Extract from input field

    if (newPassword !== confirmNewPassword) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Passwords do not match.",
          variant: "destructive",
        },
      ]);

      return;
    }

    //  // Check if password is strong before submission
    // if (!isPasswordValid) {
    //   setToastList((prev) => [
    //     ...prev,
    //     {
    //       id: Date.now(),
    //       title: "ERROR!",
    //       description:
    //         "Weak password! Must have 8+ characters, uppercase, lowercase, number, and special character.",
    //       variant: "destructive",
    //     },
    //   ]);
    //   setIsLoading(false);
    //   return;
    // }

    if (!passwordResetToken) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Invalid or missing token.",
          variant: "destructive",
        },
      ]);

      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/user/reset-password/${passwordResetToken}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          },
          body: JSON.stringify({ newPassword, confirmNewPassword }),
        },
      );

      const responseData = await response.json();
      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              responseData.data?.message || "Password reset successfully.",
            variant: "default",
          },
        ]);

        setTimeout(() => {
          setIsLoading(false);
          navigate("/signin");
        }, 3000);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message || "Failed to reset password.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      console.error("Error:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-screen tw-items-center tw-w-full tw-justify-center">
      <Card className="tw-w-full tw-shadow-none tw-border-none md:tw-max-w-2xl">
        <CardHeader>
          <CardTitle className="tw-text-xl md:tw-text-3xl">
            Reset Password
          </CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Label htmlFor="passwordResetToken">Password Reset Token</Label>
            <div className="tw-relative">
              <Input
                id="passwordResetToken"
                type="text"
                placeholder="Enter password reset token"
                required
              />
            </div>

            <Label htmlFor="newPassword" className="tw-mt-4">
              New Password
            </Label>
            <div className="tw-relative">
              <Input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {(isPasswordFocused || !isPasswordValid) && (
              <div className="tw-text-xs tw-mt-2 tw-space-y-1">
                <p
                  className={
                    passwordRequirements.length
                      ? "tw-text-green-600"
                      : "tw-text-gray-500"
                  }
                >
                  {passwordRequirements.length ? "✅" : "⬜"} At least 8
                  characters
                </p>

                <p
                  className={
                    passwordRequirements.uppercase
                      ? "tw-text-green-600"
                      : "tw-text-gray-500"
                  }
                >
                  {passwordRequirements.uppercase ? "✅" : "⬜"} One uppercase
                  letter (A–Z)
                </p>

                <p
                  className={
                    passwordRequirements.lowercase
                      ? "tw-text-green-600"
                      : "tw-text-gray-500"
                  }
                >
                  {passwordRequirements.lowercase ? "✅" : "⬜"} One lowercase
                  letter (a–z)
                </p>

                <p
                  className={
                    passwordRequirements.number
                      ? "tw-text-green-600"
                      : "tw-text-gray-500"
                  }
                >
                  {passwordRequirements.number ? "✅" : "⬜"} One number (0–9)
                </p>

                <p
                  className={
                    passwordRequirements.specialChar
                      ? "tw-text-green-600"
                      : "tw-text-gray-500"
                  }
                >
                  {passwordRequirements.specialChar ? "✅" : "⬜"} One special
                  character (!@#$%)
                </p>
              </div>
            )}

            <Label htmlFor="confirmNewPassword" className="tw-mt-4">
              Confirm New Password
            </Label>
            <div className="tw-relative">
              <Input
                id="confirmNewPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                disabled={!isPasswordValid}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {passwordsMatch && (
              <p className="tw-text-xs tw-text-green-600 tw-font-medium tw-mt-1">
                ✅ Password match
              </p>
            )}

            {showPasswordMismatch && (
              <p className="tw-text-xs tw-text-red-500 tw-font-medium tw-mt-1">
                ❌ Password does not match
              </p>
            )}

            <Button
              type="submit"
              className="tw-w-full tw-font-semibold tw-mt-6"
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default ResetPassword;
